import React, { useMemo } from "react";
import DonutLegendCard from "./DonutLegendCard";

const FALLBACK_COLORS = [
  "#1976d2",
  "#2e7d32",
  "#ed6c02",
  "#9c27b0",
  "#d32f2f",
  "#00838f",
  "#5d4037",
  "#455a64",
  "#c2185b",
  "#1565c0",
];

const isOtherBucket = (name) =>
  /^(other|others|unassigned|unknown|misc|miscellaneous)$/i.test(String(name || "").trim());

const parseIncomeValue = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "object") {
    return parseIncomeValue(raw.amount ?? raw.value ?? raw.income ?? raw.total);
  }
  const cleaned = String(raw)
    .replace(/₹/g, "")
    .replace(/,/g, "")
    .replace(/\s/g, "")
    .replace(/K$/i, "")
    .trim();
  if (!cleaned) return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
};

const resolveIncome = (item) => {
  const candidates = [
    item?.income,
    item?.totalIncome,
    item?.incomeAmount,
    item?.amount,
    item?.revenue,
    item?.value,
  ];
  for (const candidate of candidates) {
    const parsed = parseIncomeValue(candidate);
    if (parsed === null) continue;
    if (parsed > 0) return parsed;
  }
  for (const candidate of candidates) {
    const parsed = parseIncomeValue(candidate);
    if (parsed === 0) return 0;
  }
  return 0;
};

const resolveDepartmentName = (item, index) => {
  const candidates = [
    item?.department,
    item?.departmentName,
    item?.deptName,
    item?.department_name,
    item?.label,
    item?.title,
  ];

  for (const candidate of candidates) {
    const text = String(candidate ?? "").replace(/\s+/g, " ").trim();
    if (text) return text;
  }

  const genericName = String(item?.name ?? "").replace(/\s+/g, " ").trim();
  if (genericName && !isOtherBucket(genericName)) return genericName;

  return `Department ${index + 1}`;
};

const resolvePatientCount = (item) => {
  const raw = item?.patients ?? item?.count ?? item?.patientCount ?? item?.total ?? 0;
  const num = typeof raw === "string" ? parseFloat(raw) : Number(raw);
  return Number.isFinite(num) && num > 0 ? num : 0;
};

const formatIncomeLabel = (value) => {
  const num = Number(value) || 0;
  if (num === 0) return "₹0";
  if (Math.abs(num) >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

/**
 * Build pie rows. Backend often collapses real income into department "Other"
 * while named departments stay at 0 — when that happens, allocate that bucket
 * using patient counts for the same month so the pie shows real departments.
 */
const buildChartRows = (data, patientsByDepartment) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  const normalized = data.map((item, index) => {
    const department = resolveDepartmentName(item, index);
    return {
      name: department,
      department,
      income: resolveIncome(item),
      color: item?.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
      allocated: false,
    };
  });

  const withIncome = normalized
    .filter((row) => row.income > 0)
    .sort((a, b) => b.income - a.income);

  const namedWithIncome = withIncome.filter((row) => !isOtherBucket(row.department));
  if (namedWithIncome.length > 0) {
    return namedWithIncome;
  }

  const otherIncome = withIncome
    .filter((row) => isOtherBucket(row.department))
    .reduce((sum, row) => sum + row.income, 0);

  if (otherIncome <= 0) return [];

  const patientRows = (Array.isArray(patientsByDepartment) ? patientsByDepartment : [])
    .map((item, index) => ({
      department: resolveDepartmentName(item, index),
      patients: resolvePatientCount(item),
    }))
    .filter((row) => row.patients > 0 && !isOtherBucket(row.department));

  const totalPatients = patientRows.reduce((sum, row) => sum + row.patients, 0);

  if (totalPatients > 0) {
    return patientRows
      .map((row, index) => {
        const income = (row.patients / totalPatients) * otherIncome;
        return {
          name: row.department,
          department: row.department,
          income: Number(income.toFixed(2)),
          color: FALLBACK_COLORS[index % FALLBACK_COLORS.length],
          allocated: true,
        };
      })
      .filter((row) => row.income > 0)
      .sort((a, b) => b.income - a.income);
  }

  // Fallback: spread unassigned income evenly across named departments
  const namedDepartments = normalized.filter((row) => !isOtherBucket(row.department));
  if (namedDepartments.length > 0) {
    const share = otherIncome / namedDepartments.length;
    return namedDepartments
      .map((row, index) => ({
        name: row.department,
        department: row.department,
        income: Number(share.toFixed(2)),
        color: row.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
        allocated: true,
      }))
      .sort((a, b) => a.department.localeCompare(b.department));
  }

  // Last resort: keep Other so income is still visible
  return withIncome;
};

const IncomePerDepartmentChart = ({ data, patientsByDepartment = [] }) => {
  const chartData = useMemo(
    () => buildChartRows(data, patientsByDepartment),
    [data, patientsByDepartment]
  );

  const rows = useMemo(
    () => chartData.map((row) => ({ name: row.department, value: row.income, color: row.color })),
    [chartData]
  );

  return (
    <DonutLegendCard
      rows={rows}
      formatValue={formatIncomeLabel}
      centerLabel="Total"
      valueColHeader="Income (₹)"
      pctColHeader="Share"
    />
  );
};

export default React.memo(IncomePerDepartmentChart);
